import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { theme } from '@Guidelines/Theme/theme';
import PreparationSheet from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Sheets/PreparationSheet';
import ImportAudioPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection/Sheets/RecordInterviewSheet/ImportAudio/ImportAudioPanel';
import { css } from '@emotion/react';
import { FileAudio, Mic } from 'lucide-react';
import * as React from 'react';

const choicesStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

const choiceStyle = css`
	display: flex;
	align-items: center;
	gap: 0.875rem;
	padding: 1rem 1.125rem;
	text-align: left;
	cursor: pointer;
	border-radius: 18px;
`;

const primaryChoiceStyle = css`
	${choiceStyle}
	padding: 1.125rem;
	background-color: ${theme.colors.accent};
	color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.accent};

	&:hover,
	&:focus-visible {
		background-color: ${theme.colors.accentDark};
	}
`;

const secondaryChoiceStyle = css`
	${choiceStyle}
	background-color: ${theme.colors.surface};
	color: ${theme.colors.ink};
	border: 1.5px solid ${theme.colors.borderStrong};

	&:hover,
	&:focus-visible {
		border-color: ${theme.tints.amber.solid};
	}
`;

const primaryIconStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: 2.75rem;
	height: 2.75rem;
	border-radius: ${theme.radii.small};
	background-color: rgba(255, 255, 255, 0.18);
`;

const secondaryIconStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: 2.5rem;
	height: 2.5rem;
	border-radius: 13px;
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.solid};
`;

const textStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.1875rem;
`;

const primaryTitleStyle = css`
	font-family: ${theme.fonts.display};
	font-size: 1.0625rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.25;
`;

const secondaryTitleStyle = css`
	font-family: ${theme.fonts.display};
	font-size: 0.9375rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.25;
`;

const primaryDetailStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.45;
	color: rgba(255, 255, 255, 0.88);
`;

const secondaryDetailStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.45;
	color: ${theme.colors.inkMuted};
`;

type Mode = 'choose' | 'import';

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	readonly visible: boolean;
	readonly onHide: () => void;
	readonly onRecord: () => void;
};

/**
 * « Enregistrer un entretien »: record now (consent first) or import an audio file, the file being
 * dropped in the sheet itself. Nothing is generated. The sheet always reopens on the choice.
 */
const RecordInterviewSheet: React.FC<Props> = ({ subject, preparationId, visible, onHide, onRecord }) => {
	const [mode, setMode] = React.useState<Mode>('choose');

	const handleHide = React.useCallback(() => {
		setMode('choose');
		onHide();
	}, [onHide]);

	const handleImport = React.useCallback(() => {
		setMode('import');
	}, []);

	const handleBack = React.useCallback(() => {
		setMode('choose');
	}, []);

	return (
		<PreparationSheet
			visible={visible}
			onHide={handleHide}
			onBack={mode === 'import' ? handleBack : undefined}
			header={mode === 'import' ? 'Importer un fichier audio' : 'Enregistrer un entretien'}
			subtitle={
				mode === 'import'
					? 'Un entretien enregistré avec un autre appareil.'
					: 'L’entretien est transcrit, puis ajouté aux éléments de la préparation. Rien n’est généré.'
			}
		>
			{mode === 'import' ? (
				<ImportAudioPanel subject={subject} preparationId={preparationId} onImported={handleHide} />
			) : (
				<div css={choicesStyle}>
					<button type="button" css={primaryChoiceStyle} onClick={onRecord}>
						<span css={primaryIconStyle}>
							<Mic size={20} aria-hidden="true" />
						</span>
						<span css={textStyle}>
							<span css={primaryTitleStyle}>Enregistrer maintenant</span>
							<span css={primaryDetailStyle}>
								Le consentement est recueilli, puis l’enregistrement démarre.
							</span>
						</span>
					</button>
					<button type="button" css={secondaryChoiceStyle} onClick={handleImport}>
						<span css={secondaryIconStyle}>
							<FileAudio size={20} aria-hidden="true" />
						</span>
						<span css={textStyle}>
							<span css={secondaryTitleStyle}>Importer un fichier audio</span>
							<span css={secondaryDetailStyle}>
								Un enregistrement fait avec un autre appareil · m4a, mp3, wav
							</span>
						</span>
					</button>
				</div>
			)}
		</PreparationSheet>
	);
};

export default RecordInterviewSheet;
